/**
 * Compatibility hooks to ease migration from hookrouter to React Router
 * These hooks provide similar APIs to hookrouter while using React Router internally
 */
import { useLocation, useNavigate, useSearchParams, useParams, matchPath, Link } from 'react-router-dom';
import React, { useCallback, useMemo } from 'react';

/**
 * Replacement for hookrouter's usePath()
 * Returns the current pathname
 */
export const usePath = (): string => {
  const location = useLocation();
  return location.pathname;
};

/**
 * Replacement for hookrouter's navigate()
 * Returns a function to navigate programmatically
 */
export const useAppNavigate = () => {
  const navigateFn = useNavigate();
  return useCallback((path: string, replace?: boolean) => {
    navigateFn(path, { replace });
  }, [navigateFn]);
};

/**
 * Standalone navigate function for non-hook contexts
 * Note: This requires the component to be within Router context
 * Signature matches hookrouter: navigate(path, replace?, queryParams?)
 */
let navigateRef: ((path: string, replace?: boolean, queryParams?: Record<string, any>) => void) | null = null;

export const setNavigateRef = (nav: (path: string, replace?: boolean, queryParams?: Record<string, any>) => void) => {
  navigateRef = nav;
};

export const navigate = (path: string, replace?: boolean, queryParams?: Record<string, any>) => {
  // Build URL with query params if provided
  let finalPath = path;
  if (queryParams && Object.keys(queryParams).length > 0) {
    const url = new URL(path, window.location.origin);
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    });
    finalPath = url.pathname + url.search;
  }

  if (navigateRef) {
    navigateRef(finalPath, replace);
  } else {
    // Fallback to window.location for cases outside Router context
    if (replace) {
      window.location.replace(finalPath);
    } else {
      window.location.href = finalPath;
    }
  }
};

/**
 * Replacement for hookrouter's useQueryParams()
 * Returns query parameters as an object
 * Allows undefined values to remove params
 * Uses 'any' types to match hookrouter's loose typing
 */
export const useQueryParams = (): [Record<string, any>, (params: Record<string, any>) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const result: Record<string, any> = {};
    searchParams.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }, [searchParams]);

  const setParams = useCallback((newParams: Record<string, any>) => {
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);
      Object.entries(newParams).forEach(([key, value]) => {
        if (value === undefined || value === null) {
          updated.delete(key);
        } else {
          updated.set(key, String(value));
        }
      });
      return updated;
    });
  }, [setSearchParams]);

  return [params, setParams];
};

/**
 * Replacement for hookrouter's setQueryParams()
 * Sets query parameters
 */
export const useSetQueryParams = () => {
  const [, setSearchParams] = useSearchParams();

  return useCallback((params: Record<string, string>) => {
    setSearchParams(params);
  }, [setSearchParams]);
};

/**
 * Replacement for hookrouter's useRoutes()
 * Takes a hookrouter-style routes object and returns the matching component
 *
 * @param routes - Object with path patterns as keys and component factories as values
 * @returns The matched component or null
 */
export const useRoutes = (routes: Record<string, any>): any => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Try to match each route pattern
  for (const [pattern, componentFactory] of Object.entries(routes)) {
    // Skip if not a function
    if (typeof componentFactory !== 'function') continue;

    // Convert hookrouter pattern to React Router pattern
    // hookrouter uses :param and * patterns, similar to React Router
    const match = matchPath(
      {
        path: pattern,
        end: !pattern.includes('*'), // Allow partial match for wildcard routes
      },
      currentPath
    );

    if (match) {
      // Extract params from the match
      const params = match.params || {};

      // Call the component factory with the params
      // hookrouter pattern: ({ id }) => <Component id={id} />
      try {
        return componentFactory(params);
      } catch (e) {
        console.error('Error rendering route:', pattern, e);
        return null;
      }
    }
  }

  return null;
};

/**
 * Hook to get route params (replacement for accessing params in route handlers)
 */
export { useParams };

/**
 * Hook to get current location
 */
export { useLocation };

/**
 * Standalone setQueryParams function for non-hook contexts
 * Note: This requires a reference to be set from within the Router context
 */
let setQueryParamsRef: ((params: Record<string, any>) => void) | null = null;

export const setSetQueryParamsRef = (setter: (params: Record<string, any>) => void) => {
  setQueryParamsRef = setter;
};

export const setQueryParams = (params: Record<string, any>) => {
  if (setQueryParamsRef) {
    setQueryParamsRef(params);
  } else {
    // Fallback: update URL search params directly
    const url = new URL(window.location.href);
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, String(value));
      }
    });
    window.history.pushState({}, '', url.toString());
  }
};

/**
 * Replacement for hookrouter's A component
 * A link component that uses 'href' prop for backward compatibility
 */
interface AProps {
  href: string;
  children?: React.ReactNode;
  className?: string;
  onClick?: React.MouseEventHandler;
  [key: string]: any;
}

export const A: React.FC<AProps> = ({ href, children, ...rest }) => {
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
};

A.displayName = 'A';

/**
 * Component to set up navigation references
 * Must be rendered inside Router context
 */
export const NavigationSetup: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const navigateFn = useNavigate();
  const [, setSearchParams] = useSearchParams();

  // Use refs to store the latest values without causing re-renders
  const navigateFnRef = React.useRef(navigateFn);
  const setSearchParamsRef = React.useRef(setSearchParams);

  // Update refs on every render (sync, before effects)
  React.useLayoutEffect(() => {
    navigateFnRef.current = navigateFn;
    setSearchParamsRef.current = setSearchParams;
  });

  // Set up navigation references only once
  React.useEffect(() => {
    setNavigateRef((path: string, replace?: boolean) => {
      navigateFnRef.current(path, { replace });
    });
    setSetQueryParamsRef((params: Record<string, any>) => {
      setSearchParamsRef.current((prev) => {
        const newParams = new URLSearchParams(prev);
        Object.entries(params).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            newParams.delete(key);
          } else {
            newParams.set(key, String(value));
          }
        });
        return newParams;
      });
    });
  }, []);

  return <>{children}</>;
};
